/**
 * Drum tables, fills, the 16-character pattern codec and per-style defaults
 * (SPEC §4.3, §4.4, §4.5). Everything here is pure data + pure functions.
 *
 * A pattern string has one character per 16th step of a bar and uses the
 * velocity classes from `VELOCITY_CLASS`: `X` accent 1.0, `x` normal 0.8,
 * `o` ghost 0.5, `.` off. The parser additionally accepts `-` and `0` as off
 * and `1` as normal. Steps in emitted hits are **absolute** (0..63).
 *
 * Table policy per style × density:
 * - `medium` — the tables verbatim from SPEC §4.4.
 * - `sparse` — kick + backbeat (snare, or clap for house) only, ghost kicks dropped.
 * - `busy` — medium plus extra ghost kicks and 16th hats (`o` on every e/a).
 * - The fill (bar 4 only, when requested) replaces the last four steps of the
 *   backbeat lane with a snare/clap roll at velocities .6 .7 .8 1.0.
 */
import type { ChordRhythm, Density, DrumHit, DrumLane, StyleId } from '../types';
import { ACCENT_THRESHOLD, BARS, DRUM_LANES, STEPS_PER_BAR, VELOCITY_CLASS } from '../types';

/** A drum hit as produced by the tables; `by` is set by the store on apply. */
export type PatternHit = Omit<DrumHit, 'by'>;

/** Velocity at or above which `pattern16` emits `x` rather than `o`. */
export const NORMAL_THRESHOLD = 0.65;

/** Velocities of the four 16ths of the bar-4 fill, in order (SPEC §4.4). */
export const FILL_VELOCITIES: readonly number[] = [0.6, 0.7, 0.8, 1.0];

/** Step-within-bar where the fill starts (last beat of the bar). */
export const FILL_START_STEP = 12;

/** Bar index (0-based) that carries the fill: the 4th bar. */
export const FILL_BAR = BARS - 1;

/** A 16-character all-off pattern. */
export const EMPTY_PATTERN = '.'.repeat(STEPS_PER_BAR);

type Table = Partial<Record<DrumLane, string>>;

/**
 * Drum tables, one 16-char string per lane, per style × density. Lanes not
 * listed are silent. Bars are identical (variation comes from the fill).
 */
export const DRUM_TABLES: Readonly<Record<StyleId, Readonly<Record<Density, Table>>>> = {
  lofi: {
    sparse: {
      kick: 'x.....x...x.....',
      snare: '....x.......x...',
    },
    medium: {
      kick: 'x..o..x...x.o...',
      snare: '....x.......x...',
      hat: 'x.o.x.o.x.o.x.o.',
      openhat: '..............x.',
    },
    busy: {
      kick: 'x..o..x...x.o.o.',
      snare: '....x.......x...',
      hat: 'xoooxoooxoooxooo',
      openhat: '..............x.',
    },
  },
  house: {
    sparse: {
      kick: 'x...x...x...x...',
      clap: '....x.......x...',
    },
    medium: {
      kick: 'x...x...x...x...',
      clap: '....x.......x...',
      hat: '..x...x...x...x.',
      openhat: '..x...x...x...x.',
    },
    busy: {
      kick: 'x...x...x...x..o',
      clap: '....x.......x...',
      hat: '.oxo.oxo.oxo.oxo',
      openhat: '..x...x...x...x.',
    },
  },
  synthwave: {
    sparse: {
      kick: 'x...x...x...x...',
      snare: '....x.......x...',
    },
    medium: {
      kick: 'x...x...x...x...',
      snare: '....x.......x...',
      hat: 'x.x.x.x.x.x.x.x.',
      perc: '..o...o...o...o.',
    },
    busy: {
      kick: 'x...x..ox...x..o',
      snare: '....x.......x...',
      hat: 'xoxoxoxoxoxoxoxo',
      perc: '..o...o...o...o.',
    },
  },
};

/** Per-style defaults that a `style` change applies (SPEC §2, §4.3, §4.5). */
export interface StyleDefaults {
  /** Default tempo in bpm. */
  tempo: number;
  /** Default swing 0..60. */
  swing: number;
  /** Default chord articulation. */
  chordRhythm: ChordRhythm;
  /** Bass MonoSynth oscillator type. */
  bassOsc: 'triangle' | 'fatsawtooth';
  /** Master lowpass cutoff in Hz. */
  masterLowpassHz: number;
  /** Reverb wet amount 0..1. */
  reverbWet: number;
  /** Chords+lead bus lowpass cutoff in Hz. */
  chordsLowpassHz: number;
}

/** Style defaults: lofi 82/30/pad, house 124/8/offbeat, synthwave 100/0/pulse. */
export const STYLE_DEFAULTS: Readonly<Record<StyleId, StyleDefaults>> = {
  lofi: {
    tempo: 82,
    swing: 30,
    chordRhythm: 'pad',
    bassOsc: 'triangle',
    masterLowpassHz: 5500,
    reverbWet: 0.35,
    chordsLowpassHz: 2800,
  },
  house: {
    tempo: 124,
    swing: 8,
    chordRhythm: 'offbeat',
    bassOsc: 'fatsawtooth',
    masterLowpassHz: 18000,
    reverbWet: 0.2,
    chordsLowpassHz: 6000,
  },
  synthwave: {
    tempo: 100,
    swing: 0,
    chordRhythm: 'pulse',
    bassOsc: 'fatsawtooth',
    masterLowpassHz: 18000,
    reverbWet: 0.3,
    chordsLowpassHz: 6000,
  },
};

/** The lane that carries the backbeat and the fill for a style: clap for house, snare otherwise. */
export function fillLane(style: StyleId): DrumLane {
  return style === 'house' ? 'clap' : 'snare';
}

function assertBar(bar: number): void {
  if (!Number.isInteger(bar) || bar < 0 || bar >= BARS) {
    throw new RangeError(`bar must be an integer 0..${BARS - 1}; got ${bar}`);
  }
}

/** Velocity for one pattern character, or null when the character is "off". Unknown → undefined. */
function charVelocity(c: string): number | null | undefined {
  switch (c) {
    case 'X':
      return VELOCITY_CLASS.X;
    case 'x':
    case '1':
      return VELOCITY_CLASS.x;
    case 'o':
      return VELOCITY_CLASS.o;
    case '.':
    case '-':
    case '0':
      return null;
    default:
      return undefined;
  }
}

/** Velocity class character for a velocity: `X` ≥ 0.9, `x` ≥ 0.65, `o` > 0, else `.`. */
function velocityChar(v: number): 'X' | 'x' | 'o' | '.' {
  if (v >= ACCENT_THRESHOLD) return 'X';
  if (v >= NORMAL_THRESHOLD) return 'x';
  if (v > 0) return 'o';
  return '.';
}

function sortHits<T extends PatternHit>(hits: T[]): T[] {
  return hits.sort((a, b) => a.step - b.step || DRUM_LANES.indexOf(a.lane) - DRUM_LANES.indexOf(b.lane));
}

/**
 * Drum hits for one bar of a style × density table, with absolute steps.
 * `fill` is honoured only on the 4th bar (`bar === 3`), where the last four
 * steps of the backbeat lane become the roll `FILL_VELOCITIES`. Hits are
 * sorted by step, then lane display order. Throws `RangeError` for a bar
 * outside 0..3.
 */
export function drumPattern(style: StyleId, density: Density, bar: number, fill: boolean): PatternHit[] {
  assertBar(bar);
  const table = DRUM_TABLES[style][density];
  const base = bar * STEPS_PER_BAR;
  const hits: PatternHit[] = [];
  const roll = fill && bar === FILL_BAR ? fillLane(style) : null;
  for (const lane of DRUM_LANES) {
    const pattern = table[lane];
    if (!pattern) continue;
    for (let i = 0; i < STEPS_PER_BAR; i++) {
      if (roll === lane && i >= FILL_START_STEP) continue;
      const v = charVelocity(pattern[i]!);
      if (v) hits.push({ step: base + i, lane, velocity: v });
    }
  }
  if (roll) {
    FILL_VELOCITIES.forEach((velocity, k) => {
      hits.push({ step: base + FILL_START_STEP + k, lane: roll, velocity });
    });
  }
  return sortHits(hits);
}

/** All four bars of a style × density table (fill on bar 4 when requested), absolute steps 0..63. */
export function drumPatternAllBars(style: StyleId, density: Density, fill: boolean): PatternHit[] {
  const out: PatternHit[] = [];
  for (let bar = 0; bar < BARS; bar++) out.push(...drumPattern(style, density, bar, fill));
  return out;
}

/**
 * Render one lane of one bar as a 16-character string of `X` / `x` / `o` / `.`.
 * Hits from other bars or lanes are ignored; duplicate hits on a step take
 * the loudest velocity. A bar outside 0..3 simply renders as all-off (tools
 * validate bars before calling; this keeps `get_track` from ever throwing).
 */
export function pattern16(hits: readonly PatternHit[] | readonly DrumHit[], lane: DrumLane, bar: number): string {
  const base = bar * STEPS_PER_BAR;
  const vel = new Array<number>(STEPS_PER_BAR).fill(0);
  for (const h of hits) {
    if (h.lane !== lane) continue;
    const i = h.step - base;
    if (i < 0 || i >= STEPS_PER_BAR) continue;
    if (h.velocity > vel[i]!) vel[i] = h.velocity;
  }
  return vel.map(velocityChar).join('');
}

/**
 * Parse a 16-character pattern (`X x o .`, also `- 0 1`) into hits for `lane`
 * in `bar` (absolute steps). Returns `{ error }` when the lane is unknown,
 * the bar is out of range, or the string is not exactly 16 valid characters.
 * Never throws: this sits directly behind the `set_drum_steps` tool.
 */
export function parsePattern16(pattern: string, lane: DrumLane, bar: number): PatternHit[] | { error: string } {
  if (!DRUM_LANES.includes(lane)) {
    return { error: `unknown lane "${String(lane)}"; use one of ${DRUM_LANES.join(', ')}` };
  }
  if (!Number.isInteger(bar) || bar < 0 || bar >= BARS) {
    return { error: `bar must be an integer 0..${BARS - 1}; got ${bar}` };
  }
  if (typeof pattern !== 'string' || pattern.length !== STEPS_PER_BAR) {
    return {
      error: `pattern must be exactly ${STEPS_PER_BAR} characters of X x o . (also - 0 1); got ${
        typeof pattern === 'string' ? pattern.length : typeof pattern
      }`,
    };
  }
  const hits: PatternHit[] = [];
  const base = bar * STEPS_PER_BAR;
  for (let i = 0; i < STEPS_PER_BAR; i++) {
    const c = pattern[i]!;
    const v = charVelocity(c);
    if (v === undefined) {
      return { error: `pattern character ${i + 1} is "${c}"; use X (accent), x (normal), o (ghost) or . (off)` };
    }
    if (v) hits.push({ step: base + i, lane, velocity: v });
  }
  return hits;
}

/** Every lane of one bar as a 16-character string. */
export function lanesToStrings(hits: readonly PatternHit[] | readonly DrumHit[], bar: number): Record<DrumLane, string> {
  const out = {} as Record<DrumLane, string>;
  for (const lane of DRUM_LANES) out[lane] = pattern16(hits, lane, bar);
  return out;
}

/** Bar keys used by `describeDrums` when a lane differs across bars. */
export type BarKey = 'b1' | 'b2' | 'b3' | 'b4';

/** Compact per-lane description: `{ all }` when the four bars are identical, else one string per bar. */
export type LaneDescription = { all: string } | Record<BarKey, string>;

/**
 * Describe a whole drum track for `get_track`: each lane collapses to
 * `{ all: '…' }` when its four bars are identical (an empty lane is
 * `{ all: '................' }`), otherwise `{ b1, b2, b3, b4 }`.
 */
export function describeDrums(hits: readonly PatternHit[] | readonly DrumHit[]): Record<DrumLane, LaneDescription> {
  const out = {} as Record<DrumLane, LaneDescription>;
  for (const lane of DRUM_LANES) {
    const bars = Array.from({ length: BARS }, (_, bar) => pattern16(hits, lane, bar));
    const first = bars[0]!;
    out[lane] = bars.every((s) => s === first)
      ? { all: first }
      : { b1: first, b2: bars[1]!, b3: bars[2]!, b4: bars[3]! };
  }
  return out;
}

/** Sorted, de-duplicated absolute steps (0..63) that carry a kick — used by the `follow_kick` bass pattern. */
export function kickSteps(hits: readonly PatternHit[] | readonly DrumHit[]): number[] {
  const steps = new Set<number>();
  for (const h of hits) if (h.lane === 'kick') steps.add(h.step);
  return [...steps].sort((a, b) => a - b);
}
